'use client';

import { useState, type ReactNode } from 'react';
import type {
  ApplicationScanEffort,
  ScanEffortLevel,
} from '@terra/shared-contracts-applications';
import { useTranslations } from 'next-intl';
import { MetaDefault } from '../../components/typography';
import { SettingsCard } from '../settings/SettingsCard';
import { SettingsRow } from '../settings/SettingsRow';
import { toasterApi } from '../toast/ToastProvider';
import { ScanEffortEditDialog } from './ScanEffortEditDialog';
import { useScanEffortLevelOptions } from './useScanEffortLevelOptions';

export type ApplicationScanEffortCardProps = {
  scanEffort: ApplicationScanEffort | undefined;
  canManage: boolean;
  isUpdating: boolean;
  onUpdate: (level: ScanEffortLevel) => Promise<unknown>;
  renderTenantOverrideLink?: (label: ReactNode) => ReactNode;
};

export const ApplicationScanEffortCard = ({
  scanEffort,
  canManage,
  isUpdating,
  onUpdate,
  renderTenantOverrideLink,
}: ApplicationScanEffortCardProps) => {
  const t = useTranslations('designSystem.scanEffort');
  const tCommon = useTranslations('designSystem.common');
  const levelOptions = useScanEffortLevelOptions();
  const [draftLevel, setDraftLevel] = useState<ScanEffortLevel | null>(null);

  if (!scanEffort?.enabled) {
    return null;
  }

  const isOverridden = scanEffort.tenantOverride !== null;
  const overrideLabel = (
    <MetaDefault
      color="fg.muted"
      textDecoration={renderTenantOverrideLink ? 'underline' : undefined}
    >
      {t('application.overriddenByTenant')}
    </MetaDefault>
  );

  const handleSubmit = async (level: ScanEffortLevel) => {
    try {
      await onUpdate(level);
      toasterApi.success({ title: t('updated') });
      setDraftLevel(null);
    } catch (error: unknown) {
      toasterApi.error({
        title: t('updateFailed'),
        description: error instanceof Error ? error.message : undefined,
      });
    }
  };

  return (
    <>
      <SettingsCard>
        <SettingsRow
          label={t('application.title')}
          tooltip={t('application.tooltip')}
          value={t(`levels.${scanEffort.effective.level}`)}
          description={
            isOverridden
              ? (renderTenantOverrideLink?.(overrideLabel) ?? overrideLabel)
              : undefined
          }
          action={{
            label: tCommon('edit'),
            onClick: () => setDraftLevel(scanEffort.applicationLevel),
            disabled: isOverridden || isUpdating,
          }}
          isReadOnly={!canManage}
        />
      </SettingsCard>
      <ScanEffortEditDialog
        draft={draftLevel}
        current={scanEffort.applicationLevel}
        options={levelOptions}
        info={t('application.levelHint')}
        loading={isUpdating}
        onDraftChange={setDraftLevel}
        onSubmit={handleSubmit}
      />
    </>
  );
};
